/**
 * Client-side engine for camera-based part identification.
 *
 * Both halves of the matching pipeline run in the browser, so the (Alpine)
 * backend needs no ML dependencies — it only stores and ranks the vectors:
 *
 * - {@link renderModelViews} snapshots a handful of views of a part's 3D model
 *   using online-3d-viewer (already shipped for the in-page viewer).
 * - {@link embedImages} turns images (model renders or a camera frame) into
 *   L2-normalised feature vectors with transformers.js (CLIP, via ONNX/WASM).
 *
 * The heavy bits (the CLIP weights, three.js) are dynamically imported so they
 * stay out of the main bundle and only load when identification is used.
 */
import { t } from '@lingui/core/macro';

// Embedding model. Must be identical for indexing (model renders) and querying
// (camera frame) or the vectors aren't comparable. Sent to the backend as
// `model_id` so stale vectors from a different model are ignored.
//
// DINOv2 is a pure vision model purpose-built for image feature extraction
// (unlike CLIP, which needs text input and isn't handled by the
// `image-feature-extraction` pipeline).
const IDENTIFY_MODEL_NAME = 'Xenova/dinov2-small';

// q8 (int8-quantised) weights — ~22 MB download, and crucially a much smaller
// WASM heap footprint than fp32 (~88 MB). iPad Safari aborts fp32 with
// `RangeError: Out of memory` → ORT then reports "no available backend found".
// Accuracy loss is negligible for cosine ranking.
const IDENTIFY_DTYPE = 'q8';

// The chosen dtype is part of the embedding-space identity — different dtypes
// of the same checkpoint produce slightly different vectors — so it's part of
// the `model_id` sent to the backend. Changing either side invalidates the
// existing index (vectors with a different `model_id` are ignored).
export const IDENTIFY_MODEL_ID = `${IDENTIFY_MODEL_NAME}@${IDENTIFY_DTYPE}`;

// Views rendered per model for the gallery. More views = better recall but
// slower indexing.
export const IDENTIFY_VIEW_COUNT = 6;

// Render/embed resolution. CLIP resizes to 224 internally, so there's little
// point rendering larger.
const IMAGE_SIZE = 224;

// transformers.js feature extractor, lazily created and shared.
let extractorPromise: Promise<any> | null = null;

async function getExtractor(): Promise<any> {
  if (!extractorPromise) {
    const { pipeline, env } = await import('@huggingface/transformers');
    // We never ship local model files; always fetch from the HF hub/CDN.
    env.allowLocalModels = false;
    // Memory-constrained tablets (iPad Safari, low-RAM Androids) still abort
    // ORT init with `no available backend found. ERR: [wasm] RangeError: Out
    // of memory` even after the q8 switch, because the default WASM runtime
    // also allocates a multi-thread arena and a separate worker proxy on top
    // of the model weights. Force the single-thread, main-thread build to
    // strip both — q8 inference is fast enough without threading.
    const wasm = env.backends?.onnx?.wasm;
    if (wasm) {
      wasm.numThreads = 1;
      wasm.proxy = false;
    }
    extractorPromise = pipeline('image-feature-extraction', IDENTIFY_MODEL_NAME, {
      dtype: IDENTIFY_DTYPE,
      device: 'wasm'
    });
  }
  return extractorPromise;
}

/**
 * Mean-pool a model output tensor over its token dimension and L2-normalise it.
 *
 * The `image-feature-extraction` pipeline returns `last_hidden_state`
 * (`[1, tokens, dim]`) — we average the token vectors into a single `[dim]`
 * embedding ourselves, since this pipeline version exposes neither a `pooling`
 * nor a `normalize` option.
 */
function meanPoolNormalize(tensor: { dims: number[]; data: Float32Array }): number[] {
  const dims = tensor.dims;
  const data = tensor.data;
  const dim = dims[dims.length - 1];
  const pooled = new Float64Array(dim);

  if (dims.length === 3) {
    // [1, tokens, dim] — average over the token axis (batch is always 1 here).
    const tokens = dims[1];
    for (let s = 0; s < tokens; s++) {
      const off = s * dim;
      for (let d = 0; d < dim; d++) pooled[d] += data[off + d];
    }
    for (let d = 0; d < dim; d++) pooled[d] /= tokens || 1;
  } else {
    // [1, dim] or [dim] — already a single vector.
    for (let d = 0; d < dim; d++) pooled[d] = data[d];
  }

  let norm = 0;
  for (let d = 0; d < dim; d++) norm += pooled[d] * pooled[d];
  norm = Math.sqrt(norm) || 1;

  const out = new Array<number>(dim);
  for (let d = 0; d < dim; d++) out[d] = pooled[d] / norm;
  return out;
}

/**
 * Embed images into L2-normalised feature vectors.
 *
 * Accepts data-URLs / URLs / Blobs (model renders are data-URLs; the camera
 * frame is a Blob). Processed one at a time to keep peak memory low.
 */
export async function embedImages(
  sources: Array<string | Blob>
): Promise<number[][]> {
  const { RawImage } = await import('@huggingface/transformers');
  const extractor = await getExtractor();

  const vectors: number[][] = [];
  for (const src of sources) {
    const image = await RawImage.read(src);
    const output = await extractor(image);
    vectors.push(meanPoolNormalize(output));
  }
  return vectors;
}

interface RenderOptions {
  nViews?: number;
  size?: number;
  signal?: AbortSignal;
  /** Per-model timeout in ms (default {@link IDENTIFY_RENDER_TIMEOUT_MS}). */
  timeoutMs?: number;
}

// 30 s per model — occt-import-js (STEP) sometimes silently hangs inside its
// WASM parser without ever raising onLoadError, so we *have* to bound this
// from outside. A higher cap (originally 90 s) wastes minutes on parts that
// can't be rendered at all; 30 s is enough headroom for the slow-but-valid
// STEPs we've seen on this catalogue. Parts that legitimately need longer
// should ship an STL/GLB preview file.
export const IDENTIFY_RENDER_TIMEOUT_MS = 30_000;

// Skip parts whose 3D file exceeds this size via a HEAD pre-check — the
// in-browser STEP parser is heap-bound (iPad Safari OOMs around ~20 MB) and
// huge files are also the most likely to hit the timeout. Mark them as
// errored so the operator can attach a lighter preview file.
const IDENTIFY_MAX_RENDER_BYTES = 20 * 1024 * 1024;

/**
 * Probe a model URL with HEAD and return its size in bytes, or null if the
 * server doesn't expose Content-Length (CORS strips it for some hosts).
 * Network/CORS errors resolve to null so the caller can still attempt the
 * render — this is a pre-filter, not a hard gate.
 */
async function probeRenderSize(url: string): Promise<number | null> {
  try {
    const res = await fetch(url, { method: 'HEAD' });
    if (!res.ok) return null;
    const len = res.headers.get('Content-Length');
    if (!len) return null;
    const n = Number.parseInt(len, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/**
 * Wrapper around {@link renderModelViews} that short-circuits oversized
 * files with a descriptive error before launching the WASM parser. The
 * caller catches the throw and records it as a per-part indexing error.
 */
export async function renderModelViewsWithGuard(
  renderUrl: string,
  opts: RenderOptions = {}
): Promise<string[]> {
  const size = await probeRenderSize(renderUrl);
  if (size !== null && size > IDENTIFY_MAX_RENDER_BYTES) {
    const mb = (size / (1024 * 1024)).toFixed(1);
    const limitMb = (IDENTIFY_MAX_RENDER_BYTES / (1024 * 1024)).toFixed(0);
    throw new Error(
      t`3D file too large for in-browser rendering (${mb} MB, limit ${limitMb} MB). Attach an STL/GLB preview file.`
    );
  }
  return renderModelViews(renderUrl, opts);
}

/**
 * Render `nViews` images of a model around a turntable orbit, returned as
 * JPEG/PNG data-URLs. Uses an off-screen online-3d-viewer instance.
 *
 * Resolves `[]` if the URL isn't a browser-renderable mesh; rejects on load
 * failure or timeout.
 */
export async function renderModelViews(
  renderUrl: string,
  opts: RenderOptions = {}
): Promise<string[]> {
  const nViews = opts.nViews ?? IDENTIFY_VIEW_COUNT;
  const size = opts.size ?? IMAGE_SIZE;
  const OV: any = await import('online-3d-viewer');
  // Point the STEP / 3DM / Draco loaders at the locally-vendored copy so the
  // indexer doesn't depend on cdn.jsdelivr.net (see ovExternalLibs.ts).
  const { configureOvExternalLibs } = await import('../3d/ovExternalLibs');
  configureOvExternalLibs(OV);

  // WebGL needs the container in the DOM with a real size; park it off-screen
  // (not display:none, which would suspend rendering).
  const container = document.createElement('div');
  Object.assign(container.style, {
    position: 'fixed',
    left: '-10000px',
    top: '0',
    width: `${size}px`,
    height: `${size}px`
  });
  document.body.appendChild(container);

  let observer: MutationObserver | null = null;
  const teardown = (viewer?: any) => {
    observer?.disconnect();
    observer = null;
    try {
      viewer?.Destroy?.();
    } catch {
      // best-effort
    }
    container.remove();
  };

  const timeoutMs = opts.timeoutMs ?? IDENTIFY_RENDER_TIMEOUT_MS;

  return new Promise<string[]>((resolve, reject) => {
    let viewer: any;
    const timer = setTimeout(() => {
      teardown(viewer);
      // Include the URL so a logged failure points at the specific file —
      // huge or malformed STEP files are the usual culprit.
      const seconds = Math.round(timeoutMs / 1000);
      reject(
        new Error(
          `${t`3D rendering timed out after ${seconds} s.`} (${renderUrl})`
        )
      );
    }, timeoutMs);

    const onAbort = () => {
      clearTimeout(timer);
      teardown(viewer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    opts.signal?.addEventListener('abort', onAbort, { once: true });

    // online-3d-viewer reports load errors ("Failed to import model.", "The
    // model doesn't contain any meshes.", etc.) by writing them into a
    // transient overlay <div> rather than via a callback — so onModelLoaded
    // simply never fires for mesh-less STEP files and the indexer would
    // otherwise wait out the full timeoutMs on each one. Mirror the
    // MutationObserver trick from Model3DViewer to fail fast.
    // online-3d-viewer surfaces load failures by overwriting a progressDiv
    // (which during loading reads "Loading model..." / "Importing model..."
    // / "Visualizing model..."). When it switches to a failure string we
    // bail out instead of waiting out the timeout. ``textContent`` is used
    // instead of ``innerText`` because the off-screen container has no
    // layout, which can make ``innerText`` return empty in some browsers.
    const isProgressMessage = (s: string) =>
      /^(loading|importing|visualizing)\s+model/i.test(s);
    const isNoMeshes = (s: string) =>
      /no.*mesh|doesn.?t contain any meshes/i.test(s);
    observer = new MutationObserver(() => {
      const raw = (container.textContent ?? '').trim();
      if (!raw || isProgressMessage(raw)) return;
      // Once the canvas is in place the container has no readable text
      // content, so any non-progress text is treated as a failure overlay.
      clearTimeout(timer);
      opts.signal?.removeEventListener('abort', onAbort);
      teardown(viewer);
      const message = isNoMeshes(raw)
        ? t`The 3D file contains no meshes the in-browser viewer can render.`
        : t`The in-browser 3D viewer could not load the file.`;
      reject(new Error(`${message} (${renderUrl})`));
    });
    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true
    });

    try {
      viewer = new OV.EmbeddedViewer(container, {
        backgroundColor: new OV.RGBAColor(255, 255, 255, 255),
        defaultColor: new OV.RGBColor(120, 144, 168),
        edgeSettings: new OV.EdgeSettings(false, new OV.RGBColor(0, 0, 0), 1),
        environmentSettings: new OV.EnvironmentSettings([], false),
        onModelLoaded: () => {
          try {
            const v = viewer.GetViewer();
            const cam = v.GetCamera();
            const c = cam.center;
            const e = cam.eye;
            const fov = cam.fov;
            // Orbit radius = distance of the auto-fitted camera from the model.
            const radius =
              Math.hypot(e.x - c.x, e.y - c.y, e.z - c.z) || 1;

            const images: string[] = [];
            for (let i = 0; i < nViews; i++) {
              const az = (2 * Math.PI * i) / nViews;
              const el = ((i % 2 === 0 ? 20 : -10) * Math.PI) / 180;
              const eye = new OV.Coord3D(
                c.x + radius * Math.cos(el) * Math.sin(az),
                c.y + radius * Math.sin(el),
                c.z + radius * Math.cos(el) * Math.cos(az)
              );
              v.SetCamera(
                new OV.Camera(
                  eye,
                  new OV.Coord3D(c.x, c.y, c.z),
                  new OV.Coord3D(0, 1, 0),
                  fov
                )
              );
              images.push(v.GetImageAsDataUrl(size, size, false));
            }

            clearTimeout(timer);
            opts.signal?.removeEventListener('abort', onAbort);
            teardown(viewer);
            resolve(images);
          } catch (err) {
            clearTimeout(timer);
            teardown(viewer);
            reject(err);
          }
        }
      });
      viewer.LoadModelFromUrlList([renderUrl]);
    } catch (err) {
      clearTimeout(timer);
      teardown(viewer);
      reject(err);
    }
  });
}
